import { type FormEvent, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createInvoice, getSuggestedLineItems } from '../../api/invoices'
import { getMyPractices } from '../../api/staff'
import { getClient, listClients } from '../../api/clients'
import { getPatient } from '../../api/patients'
import { listConsultations, getConsultation } from '../../api/clinical'
import { listAppointments, getAppointment } from '../../api/appointments'
import { listServices } from '../../api/services'
import { listProducts } from '../../api/stock'
import { Button } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, selectClass } from '../../components/ui/FormField'
import { NumberStepper } from '../../components/ui/NumberStepper'
import { useToast } from '../../components/ui/Toast'
import { formatCurrency } from '@/lib/currency'
import type { MyPractice, ConsultationListItem } from '../../types/clinical'
import type { DiscountType, LineItemCreate } from '../../types/invoices'
import type { ClientListItem } from '../../types/clients'
import type { PatientListItem } from '../../types/patients'
import type { AppointmentListItem } from '../../types/appointments'
import type { ServiceRead } from '../../types/service'
import type { ProductListItem } from '../../types/stock'
import { SPECIES_LABELS } from '../../types/patients'

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function applyDiscount(gross: number, type: DiscountType | null, value: number | null): number {
  if (type == null || value == null || Number.isNaN(value)) return gross
  const discounted = type === 'percent' ? gross * (1 - value / 100) : gross - value
  return Math.max(discounted, 0)
}

function computePreview(
  items: DraftLineItem[],
  taxRatePercent: string,
  discountType: DiscountType | null,
  discountValue: string,
) {
  const taxRate = (parseFloat(taxRatePercent) || 0) / 100
  const lineTotals: number[] = []
  let preDiscountSubtotal = 0

  for (const it of items) {
    const gross = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0)
    const dValue = it.discount_value === '' ? null : parseFloat(it.discount_value)
    const discountedGross = applyDiscount(gross, it.discount_type, dValue)
    lineTotals.push(discountedGross)
    const lineNet = it.tax_inclusive ? discountedGross / (1 + taxRate) : discountedGross
    preDiscountSubtotal += lineNet
  }

  const invDiscountValue = discountValue === '' ? null : parseFloat(discountValue)
  const subtotal = applyDiscount(preDiscountSubtotal, discountType, invDiscountValue)
  const discountAmount = preDiscountSubtotal - subtotal
  const tax = subtotal * taxRate
  const total = subtotal + tax

  return { lineTotals, preDiscountSubtotal, subtotal, discountAmount, tax, total }
}

function fmtConsultation(date: string, reason: string | null): string {
  const d = new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  return reason ? `${d} — ${reason}` : d
}
function fmtAppointment(scheduledAt: string, reason: string | null): string {
  const d = new Date(scheduledAt).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  return reason ? `${d} — ${reason}` : d
}

interface DraftLineItem {
  description: string
  quantity: string
  unit_price: string
  tax_inclusive: boolean
  discount_type: DiscountType | null
  discount_value: string
}

const EMPTY_ITEM: DraftLineItem = {
  description: '', quantity: '1', unit_price: '0.00',
  tax_inclusive: false, discount_type: null, discount_value: '',
}

function isPristine(items: DraftLineItem[]): boolean {
  return items.length === 1 && !items[0].description.trim() && (parseFloat(items[0].unit_price) || 0) === 0
}

interface CatalogEntry {
  key: string
  label: string
  unit_price: string
  tax_inclusive: boolean
}

export default function InvoiceForm() {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()

  const searchParams = new URLSearchParams(location.search)
  const preClientId       = searchParams.get('client_id')       ? Number(searchParams.get('client_id'))       : null
  const prePatientId      = searchParams.get('patient_id')      ? Number(searchParams.get('patient_id'))      : null
  const preConsultationId = searchParams.get('consultation_id') ? Number(searchParams.get('consultation_id')) : null
  const preAppointmentId  = searchParams.get('appointment_id')  ? Number(searchParams.get('appointment_id'))  : null

  // Practice
  const [practices, setPractices] = useState<MyPractice[]>([])
  const [practiceId, setPracticeId] = useState('')
  const [loading, setLoading] = useState(true)

  // Client
  const [clientSearch, setClientSearch] = useState('')
  const [clientSuggestions, setClientSuggestions] = useState<ClientListItem[]>([])
  const [showClientDrop, setShowClientDrop] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState<number | null>(preClientId)
  const [selectedClientName, setSelectedClientName] = useState('')
  const clientRef = useRef<HTMLDivElement>(null)
  const clientTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Patient
  const [clientPatients, setClientPatients] = useState<PatientListItem[]>([])
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(prePatientId)
  const [selectedPatientName, setSelectedPatientName] = useState('')

  // Consultation (optional link)
  const [consultations, setConsultations] = useState<ConsultationListItem[]>([])
  const [selectedConsultationId, setSelectedConsultationId] = useState<number | null>(preConsultationId)
  const [selectedConsultationLabel, setSelectedConsultationLabel] = useState('')

  // Appointment (optional link)
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([])
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<number | null>(preAppointmentId)
  const [selectedAppointmentLabel, setSelectedAppointmentLabel] = useState('')

  // Form fields
  const [issuedDate, setIssuedDate] = useState(todayISO())
  const [dueDate, setDueDate] = useState('')
  const [taxRate, setTaxRate] = useState('0')
  const [taxRateTouched, setTaxRateTouched] = useState(false)
  const [discountType, setDiscountType] = useState<DiscountType | null>(null)
  const [discountValue, setDiscountValue] = useState('')
  const [notes, setNotes] = useState('')
  const [lineItems, setLineItems] = useState<DraftLineItem[]>([{ ...EMPTY_ITEM }])
  const lineItemsRef = useRef(lineItems)
  useEffect(() => { lineItemsRef.current = lineItems }, [lineItems])

  // Catalog picker (services + products)
  const [services, setServices] = useState<ServiceRead[]>([])
  const [catalogQuery, setCatalogQuery] = useState('')
  const [productResults, setProductResults] = useState<ProductListItem[]>([])
  const [showCatalogDrop, setShowCatalogDrop] = useState(false)
  const catalogRef = useRef<HTMLDivElement>(null)
  const catalogTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Suggested line items from a linked consultation
  const [pendingSuggestions, setPendingSuggestions] = useState<DraftLineItem[] | null>(null)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({})

  // Load practices
  useEffect(() => {
    getMyPractices()
      .then(ps => {
        setPractices(ps)
        if (ps.length === 1) setPracticeId(String(ps[0].id))
      })
      .catch(() => setError('Failed to load practices.'))
      .finally(() => setLoading(false))
  }, [])

  // Pre-fill VAT rate from the selected practice's default, unless the user has edited it
  useEffect(() => {
    if (taxRateTouched || !practiceId) return
    const p = practices.find(pr => String(pr.id) === practiceId)
    if (p) setTaxRate((parseFloat(p.default_vat_rate) * 100).toFixed(2))
  }, [practiceId, practices, taxRateTouched])

  // Load the practice's service catalog for the "add from catalog" picker
  useEffect(() => {
    if (!practiceId) { setServices([]); return }
    listServices({ practice_id: Number(practiceId) }).then(setServices).catch(() => {})
  }, [practiceId])

  // Pre-fill: load client name
  useEffect(() => {
    if (!preClientId) return
    getClient(preClientId)
      .then(c => {
        setSelectedClientName(`${c.first_name} ${c.last_name}`)
        if (!prePatientId) setClientPatients(c.patients.filter(p => p.is_active))
      })
      .catch(() => {})
  }, [preClientId, prePatientId])

  // Pre-fill: load patient name
  useEffect(() => {
    if (!prePatientId) return
    getPatient(prePatientId).then(p => setSelectedPatientName(p.name)).catch(() => {})
  }, [prePatientId])

  // Pre-fill: load consultation label
  useEffect(() => {
    if (!preConsultationId) return
    getConsultation(preConsultationId)
      .then(c => setSelectedConsultationLabel(fmtConsultation(c.date, c.reason)))
      .catch(() => {})
  }, [preConsultationId])

  // Pre-fill: load appointment label
  useEffect(() => {
    if (!preAppointmentId) return
    getAppointment(preAppointmentId)
      .then(a => setSelectedAppointmentLabel(fmtAppointment(a.scheduled_at, a.reason)))
      .catch(() => {})
  }, [preAppointmentId])

  // Load patients when non-pre-filled client is selected
  useEffect(() => {
    if (!selectedClientId || preClientId) return
    getClient(selectedClientId)
      .then(c => setClientPatients(c.patients.filter(p => p.is_active)))
      .catch(() => {})
  }, [selectedClientId, preClientId])

  // Load consultations when patient is selected (only if not pre-filled)
  useEffect(() => {
    if (!selectedPatientId || preConsultationId) return
    listConsultations(selectedPatientId).then(setConsultations).catch(() => {})
  }, [selectedPatientId, preConsultationId])

  // Load appointments when patient is selected (only if not pre-filled)
  useEffect(() => {
    if (!selectedPatientId || preAppointmentId) return
    listAppointments({ patient_id: selectedPatientId }).then(setAppointments).catch(() => {})
  }, [selectedPatientId, preAppointmentId])

  // Auto-suggest line items when a consultation gets linked: the appointment type's
  // catalog fee plus anything dispensed during it. If the form is still untouched,
  // apply them directly; otherwise surface them for the user to add explicitly.
  useEffect(() => {
    if (!selectedConsultationId) return
    let cancelled = false
    getSuggestedLineItems(selectedConsultationId)
      .then(suggestions => {
        if (cancelled || suggestions.length === 0) return
        const converted: DraftLineItem[] = suggestions.map(s => ({
          description: s.description,
          quantity: s.quantity,
          unit_price: s.unit_price,
          tax_inclusive: s.tax_inclusive,
          discount_type: null,
          discount_value: '',
        }))
        if (isPristine(lineItemsRef.current)) {
          setLineItems(converted)
        } else {
          setPendingSuggestions(converted)
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [selectedConsultationId])

  // Debounced client search
  useEffect(() => {
    if (!clientSearch.trim()) {
      setClientSuggestions([])
      setShowClientDrop(false)
      return
    }
    if (clientTimer.current) clearTimeout(clientTimer.current)
    clientTimer.current = setTimeout(() => {
      listClients({ search: clientSearch, limit: 8 })
        .then(res => { setClientSuggestions(res.items); setShowClientDrop(true) })
        .catch(() => {})
    }, 280)
    return () => { if (clientTimer.current) clearTimeout(clientTimer.current) }
  }, [clientSearch])

  // Debounced catalog (product) search — services are searched client-side from the
  // already-loaded list, products are searched server-side like the client picker.
  useEffect(() => {
    if (!practiceId || !catalogQuery.trim()) {
      setProductResults([])
      return
    }
    if (catalogTimer.current) clearTimeout(catalogTimer.current)
    catalogTimer.current = setTimeout(() => {
      listProducts({ practice_id: Number(practiceId), search: catalogQuery })
        .then(setProductResults)
        .catch(() => {})
    }, 280)
    return () => { if (catalogTimer.current) clearTimeout(catalogTimer.current) }
  }, [catalogQuery, practiceId])

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clientRef.current && !clientRef.current.contains(e.target as Node)) {
        setShowClientDrop(false)
      }
      if (catalogRef.current && !catalogRef.current.contains(e.target as Node)) {
        setShowCatalogDrop(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selectClient = (c: ClientListItem) => {
    setSelectedClientId(c.id)
    setSelectedClientName(`${c.first_name} ${c.last_name}`)
    setClientSearch('')
    setShowClientDrop(false)
    setSelectedPatientId(null)
    setSelectedPatientName('')
    setClientPatients([])
    setSelectedConsultationId(null)
    setSelectedConsultationLabel('')
    setSelectedAppointmentId(null)
    setSelectedAppointmentLabel('')
    setConsultations([])
    setAppointments([])
    setFieldErrors(p => ({ ...p, clientId: undefined }))
  }

  const clearClient = () => {
    setSelectedClientId(null)
    setSelectedClientName('')
    setClientSearch('')
    setSelectedPatientId(null)
    setSelectedPatientName('')
    setClientPatients([])
    setSelectedConsultationId(null)
    setSelectedConsultationLabel('')
    setSelectedAppointmentId(null)
    setSelectedAppointmentLabel('')
    setConsultations([])
    setAppointments([])
  }

  const updateItem = <K extends keyof DraftLineItem>(index: number, field: K, value: DraftLineItem[K]) =>
    setLineItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it))
  const addItem = () => setLineItems(prev => [...prev, { ...EMPTY_ITEM }])
  const removeItem = (index: number) => setLineItems(prev => prev.filter((_, i) => i !== index))

  const addFromCatalog = (entry: CatalogEntry) => {
    const item: DraftLineItem = {
      description: entry.label, quantity: '1', unit_price: entry.unit_price,
      tax_inclusive: entry.tax_inclusive, discount_type: null, discount_value: '',
    }
    setLineItems(prev => (isPristine(prev) ? [item] : [...prev, item]))
    setCatalogQuery('')
    setShowCatalogDrop(false)
  }

  const acceptSuggestions = () => {
    if (!pendingSuggestions) return
    setLineItems(prev => (isPristine(prev) ? pendingSuggestions : [...prev, ...pendingSuggestions]))
    setPendingSuggestions(null)
  }

  const catalogResults: CatalogEntry[] = (() => {
    const q = catalogQuery.trim().toLowerCase()
    if (!q) return []
    const fromServices: CatalogEntry[] = services
      .filter(s => s.name.toLowerCase().includes(q))
      .map(s => ({ key: `service-${s.id}`, label: s.name, unit_price: s.default_price, tax_inclusive: s.tax_inclusive_default }))
    const fromProducts: CatalogEntry[] = productResults
      .filter(p => p.default_price != null)
      .map(p => ({ key: `product-${p.id}`, label: p.name, unit_price: p.default_price as string, tax_inclusive: false }))
    return [...fromServices, ...fromProducts].slice(0, 12)
  })()

  const preview = computePreview(lineItems, taxRate, discountType, discountValue)

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (!practiceId) errs.practiceId = 'Please select a practice.'
    if (!selectedClientId) errs.clientId = 'Please select a client.'
    if (!issuedDate) errs.issuedDate = 'Issued date is required.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting || !validate()) return
    setSubmitting(true)
    setError(null)
    const validItems: LineItemCreate[] = lineItems
      .filter(it => it.description.trim())
      .map((it, i) => ({
        description: it.description.trim(),
        quantity: parseFloat(it.quantity) || 1,
        unit_price: parseFloat(it.unit_price) || 0,
        sort_order: i,
        tax_inclusive: it.tax_inclusive,
        discount_type: it.discount_type,
        discount_value: it.discount_type ? (parseFloat(it.discount_value) || 0) : null,
      }))
    try {
      const created = await createInvoice({
        practice_id: Number(practiceId),
        client_id: selectedClientId!,
        patient_id: selectedPatientId ?? null,
        consultation_id: selectedConsultationId ?? null,
        appointment_id: selectedAppointmentId ?? null,
        issued_date: issuedDate || null,
        due_date: dueDate || null,
        tax_rate: parseFloat(taxRate) / 100 || 0,
        notes: notes.trim() || null,
        discount_type: discountType,
        discount_value: discountType ? (parseFloat(discountValue) || 0) : null,
        line_items: validItems,
      })
      toast.success(`Invoice ${created.invoice_number} created.`)
      void navigate(`/invoices/${created.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setSubmitting(false)
    }
  }

  if (loading) return <div className="text-text-2 p-8">Loading…</div>

  const hasPatientContext = selectedClientId != null

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => void navigate(-1)}
          className="bg-transparent border-0 cursor-pointer text-accent text-sm font-medium p-0"
        >
          ← Back
        </button>
        <h1 className="text-[1.4rem] font-bold text-text-heading m-0">New Invoice</h1>
      </div>

      <Card>
        {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

        <form onSubmit={e => void handleSubmit(e)} noValidate>
          <SectionTitle>Practice &amp; Client</SectionTitle>

          <FormField label="Practice" required error={fieldErrors.practiceId}>
            <select
              value={practiceId}
              onChange={e => { setPracticeId(e.target.value); setFieldErrors(p => ({ ...p, practiceId: undefined })) }}
              className={selectClass(!!fieldErrors.practiceId)}
              disabled={submitting}
            >
              <option value="">Select practice…</option>
              {practices.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
          </FormField>

          {/* Client */}
          <FormField label="Client" required error={fieldErrors.clientId}>
            {preClientId ? (
              <div className="py-[0.6rem] px-[0.85rem] bg-surface-alt border border-border rounded text-[0.9rem] text-text">
                {selectedClientName || '…'}
              </div>
            ) : (
              <div ref={clientRef} className="relative">
                {selectedClientId ? (
                  <div className="flex items-center border border-border-input rounded bg-surface py-2 px-[0.85rem]">
                    <span className="flex-1 text-[0.9rem] text-text">{selectedClientName}</span>
                    <button
                      type="button"
                      onClick={clearClient}
                      className="bg-transparent border-0 cursor-pointer text-text-3 text-[1.2rem] leading-none py-0 px-[0.1rem]"
                      title="Clear"
                    >×</button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={clientSearch}
                      onChange={e => setClientSearch(e.target.value)}
                      onFocus={() => clientSearch && setShowClientDrop(true)}
                      placeholder="Search by name or email…"
                      className={inputClass(!!fieldErrors.clientId)}
                      disabled={submitting}
                    />
                    {showClientDrop && clientSuggestions.length > 0 && (
                      <div className="absolute z-[100] top-full left-0 right-0 bg-surface border border-border rounded-md shadow-[0_4px_16px_rgba(0,0,0,0.1)] mt-[2px] max-h-[220px] overflow-y-auto">
                        {clientSuggestions.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            onMouseDown={() => selectClient(c)}
                            className="block w-full py-[0.6rem] px-[0.85rem] bg-transparent border-0 border-b border-border cursor-pointer text-left text-[0.9rem] text-text hover:bg-surface-alt"
                          >
                            {c.first_name} {c.last_name}
                            <span className="text-text-3 text-[0.8rem] ml-2">{c.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {showClientDrop && clientSearch && clientSuggestions.length === 0 && (
                      <div className="absolute z-[100] top-full left-0 right-0 bg-surface border border-border rounded-md py-[0.6rem] px-[0.85rem] mt-[2px] text-[0.875rem] text-text-3">
                        No clients found.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </FormField>

          {/* Patient — shown once client is known */}
          {hasPatientContext && (
            <FormField label="Patient" hint="Optional">
              {prePatientId ? (
                <div className="py-[0.6rem] px-[0.85rem] bg-surface-alt border border-border rounded text-[0.9rem] text-text">
                  {selectedPatientName || '…'}
                </div>
              ) : (
                <select
                  value={selectedPatientId ? String(selectedPatientId) : ''}
                  onChange={e => {
                    const pid = e.target.value ? Number(e.target.value) : null
                    setSelectedPatientId(pid)
                    const p = clientPatients.find(x => x.id === pid)
                    setSelectedPatientName(p?.name ?? '')
                    setSelectedConsultationId(null)
                    setSelectedConsultationLabel('')
                    setSelectedAppointmentId(null)
                    setSelectedAppointmentLabel('')
                    setConsultations([])
                    setAppointments([])
                  }}
                  className={selectClass()}
                  disabled={submitting || clientPatients.length === 0}
                >
                  <option value="">
                    {clientPatients.length === 0 ? 'No active patients on file' : 'Select patient… (optional)'}
                  </option>
                  {clientPatients.map(p => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name} — {SPECIES_LABELS[p.species] ?? p.species}
                    </option>
                  ))}
                </select>
              )}
            </FormField>
          )}

          {/* Optional record links — shown once patient is known */}
          {selectedPatientId && (
            <>
              <SectionTitle>
                Link Records <span className="text-[0.78rem] font-normal text-text-3 normal-case tracking-normal">(optional)</span>
              </SectionTitle>

              <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
                <FormField label="Consultation" hint="Auto-suggests line items from it">
                  {preConsultationId ? (
                    <div className="py-[0.6rem] px-[0.85rem] bg-surface-alt border border-border rounded text-[0.9rem] text-text">
                      {selectedConsultationLabel || '…'}
                    </div>
                  ) : (
                    <select
                      value={selectedConsultationId ? String(selectedConsultationId) : ''}
                      onChange={e => setSelectedConsultationId(e.target.value ? Number(e.target.value) : null)}
                      className={selectClass()}
                      disabled={submitting}
                    >
                      <option value="">None</option>
                      {consultations.map(c => (
                        <option key={c.id} value={String(c.id)}>{fmtConsultation(c.date, c.reason)}</option>
                      ))}
                    </select>
                  )}
                </FormField>

                <FormField label="Appointment">
                  {preAppointmentId ? (
                    <div className="py-[0.6rem] px-[0.85rem] bg-surface-alt border border-border rounded text-[0.9rem] text-text">
                      {selectedAppointmentLabel || '…'}
                    </div>
                  ) : (
                    <select
                      value={selectedAppointmentId ? String(selectedAppointmentId) : ''}
                      onChange={e => setSelectedAppointmentId(e.target.value ? Number(e.target.value) : null)}
                      className={selectClass()}
                      disabled={submitting}
                    >
                      <option value="">None</option>
                      {appointments.map(a => (
                        <option key={a.id} value={String(a.id)}>{fmtAppointment(a.scheduled_at, a.reason)}</option>
                      ))}
                    </select>
                  )}
                </FormField>
              </div>
            </>
          )}

          <SectionTitle>Dates</SectionTitle>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Issued date" required error={fieldErrors.issuedDate}>
              <input
                type="date"
                value={issuedDate}
                onChange={e => { setIssuedDate(e.target.value); setFieldErrors(p => ({ ...p, issuedDate: undefined })) }}
                className={inputClass(!!fieldErrors.issuedDate)}
                disabled={submitting}
              />
            </FormField>
            <FormField label="Due date" hint="Optional">
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className={inputClass()}
                disabled={submitting}
              />
            </FormField>
          </div>

          <SectionTitle>Line Items</SectionTitle>

          {pendingSuggestions && (
            <div className="flex items-center justify-between gap-3 bg-accent-muted border border-accent/30 rounded-md py-2 px-3 mb-3 text-[0.85rem]">
              <span className="text-text">
                {pendingSuggestions.length} suggested item{pendingSuggestions.length > 1 ? 's' : ''} available from this consultation.
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button type="button" onClick={acceptSuggestions} className="text-accent font-semibold cursor-pointer bg-transparent border-0 p-0">
                  Add them
                </button>
                <button type="button" onClick={() => setPendingSuggestions(null)} className="text-text-3 cursor-pointer bg-transparent border-0 p-0">
                  Dismiss
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto mb-3">
            <table className="w-full border-collapse text-[0.875rem]">
              <thead>
                <tr>
                  <th className="py-2 pr-2 pl-0 text-left font-semibold text-text-2 text-[0.78rem] uppercase">Description</th>
                  <th className="p-2 text-right font-semibold text-text-2 text-[0.78rem] uppercase w-[110px]">Qty</th>
                  <th className="p-2 text-right font-semibold text-text-2 text-[0.78rem] uppercase w-[100px]">Unit Price</th>
                  <th className="p-2 text-center font-semibold text-text-2 text-[0.78rem] uppercase w-[70px]">Tax incl.</th>
                  <th className="p-2 text-center font-semibold text-text-2 text-[0.78rem] uppercase w-[140px]">Discount</th>
                  <th className="p-2 text-right font-semibold text-text-2 text-[0.78rem] uppercase w-[100px]">Total</th>
                  <th className="w-9"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, i) => (
                  <tr key={i}>
                    <td className="py-[0.3rem] pr-2 pl-0">
                      <input
                        type="text"
                        value={item.description}
                        onChange={e => updateItem(i, 'description', e.target.value)}
                        placeholder="Service or product description"
                        className={`${inputClass()} text-[0.875rem] py-[0.45rem] px-[0.6rem]`}
                        disabled={submitting}
                        maxLength={500}
                      />
                    </td>
                    <td className="p-[0.3rem_0.5rem]">
                      <NumberStepper
                        value={item.quantity}
                        onChange={v => updateItem(i, 'quantity', v)}
                        min={0.01}
                        step={1}
                        disabled={submitting}
                      />
                    </td>
                    <td className="p-[0.3rem_0.5rem]">
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={e => updateItem(i, 'unit_price', e.target.value)}
                        className={`${inputClass()} text-right text-[0.875rem] py-[0.45rem] px-[0.6rem]`}
                        min={0} step={0.01}
                        disabled={submitting}
                      />
                    </td>
                    <td className="p-[0.3rem_0.5rem] text-center">
                      <input
                        type="checkbox"
                        checked={item.tax_inclusive}
                        onChange={e => updateItem(i, 'tax_inclusive', e.target.checked)}
                        disabled={submitting}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="p-[0.3rem_0.5rem]">
                      <div className="flex items-center gap-1">
                        <select
                          value={item.discount_type ?? ''}
                          onChange={e => updateItem(i, 'discount_type', (e.target.value || null) as DiscountType | null)}
                          className="py-[0.35rem] px-1 border border-border-input rounded text-[0.8rem] outline-none bg-surface text-text"
                          disabled={submitting}
                        >
                          <option value="">None</option>
                          <option value="percent">%</option>
                          <option value="amount">R</option>
                        </select>
                        {item.discount_type && (
                          <input
                            type="number"
                            value={item.discount_value}
                            onChange={e => updateItem(i, 'discount_value', e.target.value)}
                            className="w-16 py-[0.35rem] px-1 border border-border-input rounded text-[0.8rem] outline-none text-right bg-surface text-text"
                            min={0} max={item.discount_type === 'percent' ? 100 : undefined} step={0.01}
                            disabled={submitting}
                          />
                        )}
                      </div>
                    </td>
                    <td className="p-[0.3rem_0.5rem] text-right font-semibold text-text whitespace-nowrap">
                      {formatCurrency(preview.lineTotals[i] ?? 0)}
                    </td>
                    <td className="py-[0.3rem] pl-2 pr-0 text-center">
                      {lineItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(i)}
                          className="bg-transparent border-0 cursor-pointer text-danger-text text-[1.1rem] leading-none p-[0.2rem]"
                          title="Remove"
                        >×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={addItem}
            className="bg-transparent border border-dashed border-border-input rounded text-accent cursor-pointer text-[0.875rem] font-medium py-2 px-4 w-full mb-3"
            disabled={submitting}
          >
            + Add Line Item
          </button>

          <div ref={catalogRef} className="relative mb-4">
            <input
              type="text"
              value={catalogQuery}
              onChange={e => { setCatalogQuery(e.target.value); setShowCatalogDrop(true) }}
              onFocus={() => catalogQuery && setShowCatalogDrop(true)}
              placeholder="Search services or products to add from the catalog…"
              className={inputClass()}
              disabled={submitting || !practiceId}
            />
            {showCatalogDrop && catalogResults.length > 0 && (
              <div className="absolute z-[100] top-full left-0 right-0 bg-surface border border-border rounded-md shadow-[0_4px_16px_rgba(0,0,0,0.1)] mt-[2px] max-h-[240px] overflow-y-auto">
                {catalogResults.map(entry => (
                  <button
                    key={entry.key}
                    type="button"
                    onMouseDown={() => addFromCatalog(entry)}
                    className="flex items-center justify-between w-full py-[0.55rem] px-[0.85rem] bg-transparent border-0 border-b border-border cursor-pointer text-left text-[0.875rem] text-text hover:bg-surface-alt"
                  >
                    <span>{entry.label}</span>
                    <span className="text-text-3">{formatCurrency(entry.unit_price)}</span>
                  </button>
                ))}
              </div>
            )}
            {showCatalogDrop && catalogQuery && catalogResults.length === 0 && (
              <div className="absolute z-[100] top-full left-0 right-0 bg-surface border border-border rounded-md py-[0.6rem] px-[0.85rem] mt-[2px] text-[0.875rem] text-text-3">
                No matching services or products.
              </div>
            )}
          </div>

          <div className="bg-surface-alt rounded-md py-3 px-4 mb-6 text-[0.875rem]">
            <div className="flex justify-between text-text-2 mb-[0.3rem]">
              <span>Subtotal (before discount)</span><span>{formatCurrency(preview.preDiscountSubtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-text-2 mb-[0.3rem] gap-2">
              <span className="flex items-center gap-[0.4rem]">
                Discount
                <select
                  value={discountType ?? ''}
                  onChange={e => setDiscountType((e.target.value || null) as DiscountType | null)}
                  className="py-[0.25rem] px-[0.4rem] border border-border-input rounded text-[0.82rem] outline-none bg-surface text-text"
                  disabled={submitting}
                >
                  <option value="">None</option>
                  <option value="percent">%</option>
                  <option value="amount">R</option>
                </select>
                {discountType && (
                  <input
                    type="number"
                    value={discountValue}
                    onChange={e => setDiscountValue(e.target.value)}
                    className="w-16 py-[0.25rem] px-[0.4rem] border border-border-input rounded text-[0.82rem] outline-none text-right bg-surface text-text"
                    min={0} max={discountType === 'percent' ? 100 : undefined} step={0.01}
                    disabled={submitting}
                  />
                )}
              </span>
              <span>-{formatCurrency(preview.discountAmount)}</span>
            </div>
            <div className="flex justify-between items-center text-text-2 mb-[0.3rem] gap-2">
              <span className="flex items-center gap-[0.4rem]">
                Tax (VAT)
                <input
                  type="number"
                  value={taxRate}
                  onChange={e => { setTaxRate(e.target.value); setTaxRateTouched(true) }}
                  className="w-14 py-[0.25rem] px-[0.4rem] border border-border-input rounded text-[0.82rem] outline-none text-right bg-surface text-text"
                  min={0} max={100} step={0.1}
                  disabled={submitting}
                />
                <span>%</span>
              </span>
              <span>{formatCurrency(preview.tax)}</span>
            </div>
            <div className="flex justify-between font-bold text-text text-base border-t border-border pt-[0.4rem] mt-[0.2rem]">
              <span>Total</span><span>{formatCurrency(preview.total)}</span>
            </div>
          </div>

          <SectionTitle>Notes</SectionTitle>
          <FormField label="Notes" hint="Optional">
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className={`${inputClass()} resize-y min-h-[70px]`}
              placeholder="Payment instructions, additional details…"
              disabled={submitting}
            />
          </FormField>

          <div className="flex gap-3 mt-7 pt-6 border-t border-border">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Invoice'}
            </Button>
            <Button type="button" variant="outline" onClick={() => void navigate(-1)} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
